import { useMemo, useState } from 'react';
import { Button } from '@ophelos/ui/components/button';
import { Input } from '@ophelos/ui/components/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@ophelos/ui/components/dropdown-menu';
import { Globe, Moon, SlidersVertical, Sun } from 'lucide-react';
import { type LanguageCode, useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/components/layout/ThemeProvider';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@ophelos/ui/lib/utils';

/** Below this many languages the search box costs more than it saves. */
const SEARCHABLE_THRESHOLD = 6;

interface SettingsMenuProps {
  className?: string;
  side?: 'top' | 'bottom';
  size?: 'icon' | 'icon-sm' | 'icon-lg';
}

export function SettingsMenu({ className, side = 'bottom', size = 'icon-lg' }: SettingsMenuProps) {
  const { currentLanguage, setLanguage, languages } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const selectedLang = languages.find((lang) => lang.code === currentLanguage) ?? languages[0];
  const [query, setQuery] = useState('');
  const searchable = languages.length > SEARCHABLE_THRESHOLD;
  const visibleLanguages = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? languages.filter((lang) => lang.name.toLowerCase().includes(term)) : languages;
  }, [languages, query]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className={cn('rounded-full max-md:size-9', className)}
          aria-label={t('settings.title')}
          data-slot="settings-menu"
        >
          <SlidersVertical className="size-4.5 max-md:size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={side} className="w-52">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-3">
            <Globe className="size-4 text-muted-foreground" />
            <span className="flex flex-col items-start">
              {t('settings.language')}
              <span className="text-xs text-muted-foreground">{selectedLang.name}</span>
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
            {searchable && (
              <>
                {/* Radix menus type-ahead on printable keys, so the field must not
                    swallow them for its own filtering — it is a plain input, and
                    the list below stays Radix items so arrows/Enter keep working. */}
                <div className="p-1">
                  <Input
                    autoFocus
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                    placeholder={t('settings.searchLanguage')}
                    className="h-8"
                  />
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            {visibleLanguages.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">{t('settings.noLanguage')}</div>
            ) : (
              <DropdownMenuRadioGroup value={currentLanguage} onValueChange={(value) => setLanguage(value as LanguageCode)}>
                {visibleLanguages.map((lang) => (
                  <DropdownMenuRadioItem key={lang.code} value={lang.code} className="cursor-pointer gap-2">
                    <span className="text-lg">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs">{t('settings.appearance')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as 'light' | 'dark')}>
          <DropdownMenuRadioItem value="light" className="cursor-pointer gap-2">
            <Sun className="size-4 text-muted-foreground" />
            {t('settings.light')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" className="cursor-pointer gap-2">
            <Moon className="size-4 text-muted-foreground" />
            {t('settings.dark')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
